"use client"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { financeCloseTrustApi } from "@/features/finance/services/finance-close-trust-api"
import { financeQueryKeys } from "@/features/finance/services/finance-query-keys"
import type {
  CreateReconciliationSessionInput,
  ListReconciliationSessionsQuery,
  OpeningBalancesQuery,
  ReconciliationTransitionInput,
} from "@/features/finance/model/finance-close-trust-types"
import type { ReconciliationViewerAction } from "@/features/finance/model/finance-close-trust-types"
import type { ReconciliationLineState } from "@/features/finance/model/finance-close-trust-types"

type FinanceQueryClient = ReturnType<typeof useQueryClient>

function invalidateKeys(queryClient: FinanceQueryClient, keys: ReadonlyArray<readonly unknown[]>) {
  for (const queryKey of keys) {
    queryClient.invalidateQueries({ queryKey })
  }
}

/**
 * Closing or reopening a period, or moving the fiscal year start, changes which dates accept a posting — so the
 * calendar itself and everything whose postability depends on it refetch (ADR-0007: invalidate what changed).
 */
function invalidateFiscalPeriods(queryClient: FinanceQueryClient) {
  invalidateKeys(queryClient, [
    financeQueryKeys.fiscal(),
    financeQueryKeys.transactions(),
    financeQueryKeys.accounting(),
    financeQueryKeys.openingBalances(),
  ])
}

/**
 * Reconciliation writes post journal entries (session close) or change what is still unmatched, so the
 * balances, the accounting reports and the overview projections move with the session.
 */
function invalidateReconciliation(queryClient: FinanceQueryClient) {
  invalidateKeys(queryClient, [
    financeQueryKeys.reconciliation(),
    financeQueryKeys.transactions(),
    financeQueryKeys.accounts(),
    financeQueryKeys.balances(),
    financeQueryKeys.accounting(),
    financeQueryKeys.overview(),
  ])
}

/* --- Fiscal periods (IBA-72) --- */

export function useFiscalCalendarQuery(workspaceId: string | null, branchId: string | null) {
  return useQuery({
    queryKey: financeQueryKeys.fiscalCalendar(workspaceId, branchId),
    queryFn: () => financeCloseTrustApi.getFiscalCalendar(workspaceId as string, branchId as string),
    enabled: Boolean(workspaceId && branchId),
  })
}

export function useCloseFiscalPeriodMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { workspaceId: string; branchId: string; periodId: string }) =>
      financeCloseTrustApi.closeFiscalPeriod(input),
    onSuccess: () => invalidateFiscalPeriods(queryClient),
  })
}

export function useReopenFiscalPeriodMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { workspaceId: string; branchId: string; periodId: string }) =>
      financeCloseTrustApi.reopenFiscalPeriod(input),
    onSuccess: () => invalidateFiscalPeriods(queryClient),
  })
}

export function useUpdateFiscalSettingsMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { workspaceId: string; branchId: string; fiscalYearStartMonth: number }) =>
      financeCloseTrustApi.updateFiscalSettings(input),
    onSuccess: () => invalidateFiscalPeriods(queryClient),
  })
}

/* --- Opening balances (IBA-71) --- */

export function useOpeningBalancesStateQuery(query: OpeningBalancesQuery | null) {
  return useQuery({
    queryKey: financeQueryKeys.openingBalancesState(query),
    queryFn: () => financeCloseTrustApi.getOpeningBalancesState(query as OpeningBalancesQuery),
    enabled: Boolean(query?.workspaceId),
  })
}

/* --- Account reconciliation (IBA-73) --- */

export function useReconciliationSessionsQuery(query: ListReconciliationSessionsQuery | null) {
  return useQuery({
    queryKey: financeQueryKeys.reconciliationSessions(query),
    queryFn: () => financeCloseTrustApi.listReconciliationSessions(query as ListReconciliationSessionsQuery),
    enabled: Boolean(query?.workspaceId),
  })
}

export function useReconciliationSessionQuery(input: { workspaceId: string; sessionId: string } | null) {
  return useQuery({
    queryKey: financeQueryKeys.reconciliationSession(input),
    queryFn: () => financeCloseTrustApi.getReconciliationSession(input as { workspaceId: string; sessionId: string }),
    enabled: Boolean(input?.workspaceId && input?.sessionId),
  })
}

export function useCreateReconciliationSessionMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateReconciliationSessionInput) => financeCloseTrustApi.createReconciliationSession(input),
    onSuccess: () => invalidateReconciliation(queryClient),
  })
}

/**
 * Ticking a line is TRACKING ONLY — the backend writes nothing to accounting data. Invalidating balances,
 * accounting and the overview on every checkbox made a 50-line reconciliation refetch the whole finance
 * module 50 times for figures that cannot have changed (ADR-0007: invalidate what changed).
 */
export function useSetReconciliationLineMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      workspaceId: string
      sessionId: string
      lineId: string
      state: ReconciliationLineState
      note?: string
    }) =>
      financeCloseTrustApi.setReconciliationLine(input),
    onSuccess: () => invalidateKeys(queryClient, [financeQueryKeys.reconciliation()]),
  })
}

/** "Run" — re-pull the period's transactions into an open draft. */
export function useResyncReconciliationSessionMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { workspaceId: string; sessionId: string }) =>
      financeCloseTrustApi.resyncReconciliationSession(input),
    onSuccess: () => invalidateReconciliation(queryClient),
  })
}

/** Attach or remove the statement a reconciliation was prepared against. */
export function useUploadReconciliationProofMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { workspaceId: string; sessionId: string; file: File }) =>
      financeCloseTrustApi.uploadReconciliationProof(input),
    // Attaching evidence changes no money either.
    onSuccess: () => invalidateKeys(queryClient, [financeQueryKeys.reconciliation()]),
  })
}

export function useDeleteReconciliationProofMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { workspaceId: string; sessionId: string; fileId: string }) =>
      financeCloseTrustApi.deleteReconciliationProof(input),
    onSuccess: () => invalidateKeys(queryClient, [financeQueryKeys.reconciliation()]),
  })
}

export function useDeleteReconciliationSessionMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { workspaceId: string; sessionId: string }) =>
      financeCloseTrustApi.deleteReconciliationSession(input),
    onSuccess: () => invalidateReconciliation(queryClient),
  })
}

/**
 * One mutation for every lifecycle transition — the action is an argument, not a separate hook, so the four
 * transitions cannot drift apart in what they invalidate.
 */
export function useReconciliationTransitionMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ action, ...input }: ReconciliationTransitionInput & { action: ReconciliationViewerAction }) =>
      financeCloseTrustApi.transitionReconciliationSession(action, input),
    onSuccess: () => invalidateReconciliation(queryClient),
  })
}
